import ChatTypes from "./chat.types";
import { IChatMessage, IChat, IChatQuickReply, AttachmentLoadingState } from "store/chat/chat.state";
import { ThunkDispatch } from "redux-thunk";
import RootState from "store/state";
import { batch } from "react-redux";
import { DPACheck, DPAResult } from "store/contact/contact.state";
import { getQuickRepliesWithHierarchy } from "../../services/api/api.quick-replies";
import { getChatQuickReplies } from "../../services/api/api.chat";
import { ContentBlock, EditorState } from "draft-js";

export type ChatAction =
    | { type: typeof ChatTypes.SET_SELECTED_CHAT, payload: string | null }
    | { type: typeof ChatTypes.CLEAR_UNREAD; payload: string }
    | { type: typeof ChatTypes.ADD_CHAT_CONNECTION; payload: IChat }
    | { type: typeof ChatTypes.CHAT_CONTACT_IS_TYPING; payload: { id: string; typing: boolean } }
    | { type: typeof ChatTypes.CHAT_SEND_TYPING_EVENT, payload: { id: string } }
    // | { type: typeof ChatTypes.CHAT_INCOMING; payload: string }
    | { type: typeof ChatTypes.CHAT_CONNECTED; payload: string }
    | { type: typeof ChatTypes.CHAT_DISCONNECTED; payload: string }
    | { type: typeof ChatTypes.CHAT_ACW; payload: string }
    | { type: typeof ChatTypes.CHAT_ACW_EXTEND; payload: string }
    | { type: typeof ChatTypes.CHAT_MISSED; payload: string }
    | { type: typeof ChatTypes.END_CHAT; payload: string }
    | { type: typeof ChatTypes.REMOVE_CHAT_CONNECTION; payload: string }
    | { type: typeof ChatTypes.CLEAR_CHAT_CONNECTION; payload: string }
    | { type: typeof ChatTypes.RECIEVE_CHAT_MESSAGE; payload: { id: string; msg: IChatMessage | IChatMessage[] } }
    | { type: typeof ChatTypes.SEND_CHAT_MESSAGE; payload: { id: string, msg: string; clearEditor: boolean } }
    | { type: typeof ChatTypes.ACCEPT_CHAT; payload: string }
    | { type: typeof ChatTypes.DECLINE_CHAT; payload: string }
    | { type: typeof ChatTypes.RECOVER_CHATS; payload: IChat[] }
    | { type: typeof ChatTypes.SET_UNREAD_CHATS; payload: { id: string, unread: number } }
    | { type: typeof ChatTypes.COMPLETE_CHAT_ACW; payload: string }
    | { type: typeof ChatTypes.TRANSFER_CHAT; payload: { id: string, endpoint: connect.Endpoint } }
    | { type: typeof ChatTypes.GET_QUICK_REPLIES; payload: { [key: string]: IChatQuickReply[] } }
    | { type: typeof ChatTypes.GET_QUICK_REPLIES_LOADING; }
    | { type: typeof ChatTypes.UPDATE_DPA_RESULT_CHAT; payload: { result: DPAResult; id: string } }
    | { type: typeof ChatTypes.UPDATE_AGENT_DPA_CHAT; payload: { check: DPACheck; id: string } }
    | { type: typeof ChatTypes.UPDATE_DPA_CHAT; payload: { check: DPACheck; id: string } }
    | { type: typeof ChatTypes.SET_CHAT_REPLY_TEXT; payload: { id: string; text: string; isProbablyJson: boolean } }
    | { type: typeof ChatTypes.MARK_CHAT_MESSAGE_RECEIPTS_AS_READ; payload: string }
    | { type: typeof ChatTypes.UPDATE_NOTE; payload: { id: string; note: string } }
    | { type: typeof ChatTypes.DOWNLOAD_ATTACHMENT; payload: { attachmentId: string, attachmentName: string } }
    | { type: typeof ChatTypes.DOWNLOAD_CONNECT_ATTACHMENT_FROM_TRANSCRIPT; payload: { attachmentID: string, contactID: string } }
    | { type: typeof ChatTypes.SEND_ATTACHMENT; payload: { id:string; stringMessage: string }}
    | { type: typeof ChatTypes.SET_ATTACHMENT; payload: { id: string; files:File[]}}
    | { type: typeof ChatTypes.DELETE_ATTACHMENT; payload: { id: string; index: number }}
    | { type: typeof ChatTypes.SET_ATTACHMENT_LOADING; payload: { id: string; state: AttachmentLoadingState; index?: number}}
    | { type: typeof ChatTypes.UPDATE_CONNECTION_EDITOR_CONTENT, payload: {content: EditorState, id: string,} }
    | { type: typeof ChatTypes.SET_CUSTOMER_INITIALLY_ACTIVE, payload: string }
    | { type: typeof ChatTypes.SET_DELIVERED_TIMESTAMP; payload: { chatId: string; messageId: string; timestamp: string }}
    | { type: typeof ChatTypes.SET_READ_TIMESTAMP; payload: { chatId: string; messageId: string; timestamp: string }}

export function setDeliveredTimestamp (chatId: string, messageId: string, timestamp: string) {
    return {
        type: ChatTypes.SET_DELIVERED_TIMESTAMP,
        payload: {
            chatId,
            messageId,
            timestamp,
        }
    }
}

export function setReadTimestamp (chatId: string, messageId: string, timestamp: string) {
    return {
        type: ChatTypes.SET_READ_TIMESTAMP,
        payload: {
            chatId,
            messageId,
            timestamp
        }
    }
}

export function setChatReplyText(id: string, text: string, isProbablyJson: boolean): ChatAction {
    return {
        type: ChatTypes.SET_CHAT_REPLY_TEXT,
        payload: {
            id,
            text,
            isProbablyJson
        }
    }
}

export function getQuickReplies(channels?: string) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, ChatAction>,
        getState: () => RootState
    ) => {
        try {
            const quickRepliesWithChannels = getState().chat.quickReplies;
            if (quickRepliesWithChannels && quickRepliesWithChannels[channels ?? 'ALL']) return;

            dispatch({
                type: ChatTypes.GET_QUICK_REPLIES_LOADING,
            });

            const allChannels = getState()
                .app.appConfig.channels?.map(channel => channel.value)
                .join();
            const channelsParam = channels ?? allChannels;
            const companyID = getState().app.ID;
            const instanceID = getState().app.instance?.ID || '';

            const quickReplies = await getQuickRepliesWithHierarchy(
                companyID,
                instanceID,
                channelsParam
            );

            dispatch({
                type: ChatTypes.GET_QUICK_REPLIES,
                payload: { [channels ?? 'ALL']: quickReplies.data },
            });
        } catch (error) {
            console.log('error getting quick replies');
        }
    };
}

export function downloadAttachment(attachmentId: string, attachmentName: string,): ChatAction {
    return {
        type: ChatTypes.DOWNLOAD_ATTACHMENT,
        payload: {
            attachmentId,
            attachmentName
        }
    }
}

export function downloadConnectAttachmentFromTranscript(attachmentID: string, contactID: string): ChatAction {
    return {
        type: ChatTypes.DOWNLOAD_CONNECT_ATTACHMENT_FROM_TRANSCRIPT,
        payload: {
            attachmentID,
            contactID
        }
    }
}

export function sendAttachment(id: string, stringMessage: string) : ChatAction {
    return {
        type: ChatTypes.SEND_ATTACHMENT,
        payload: {
            id,
            stringMessage,
        }
    }
}

export function setAttachment(id: string, files: File[]) : ChatAction {
    return {
        type: ChatTypes.SET_ATTACHMENT,
        payload: {
            id,
            files
        }
    }
}

export function deleteAttachment(id: string, index: number): ChatAction {
    return {
        type: ChatTypes.DELETE_ATTACHMENT,
        payload: {
            id,
            index
        }
    }
}

export function setAttachmentLoading(id: string, state: AttachmentLoadingState, index?: number) :ChatAction {
    return {
        type: ChatTypes.SET_ATTACHMENT_LOADING,
        payload: {
            id,
            index,
            state
        }
    }
}

export function clearUnread(id: string): ChatAction {
    return {
        type: ChatTypes.CLEAR_UNREAD,
        payload: id
    }
}

export function chatSendTypingEvent(id: string): ChatAction {
    return {
        type: ChatTypes.CHAT_SEND_TYPING_EVENT,
        payload: {
            id,
        }
    }
}

export function chatContactIsTyping(id: string, typing = true): ChatAction {
    return {
        type: ChatTypes.CHAT_CONTACT_IS_TYPING,
        payload: {
            id,
            typing
        }
    }
}

export function completeChatACW(id: string): ChatAction {
    return {
        type: ChatTypes.COMPLETE_CHAT_ACW,
        payload: id
    }
}

export function chatMissed(id: string): ChatAction {
    return {
        type: ChatTypes.CHAT_MISSED,
        payload: id
    }
}

export function endChat(id: string): ChatAction {
    return {
        type: ChatTypes.END_CHAT,
        payload: id
    }
}

export function setUnreadChats(id: string, unread: number): ChatAction {
    return {
        type: ChatTypes.SET_UNREAD_CHATS,
        payload: {
            id,
            unread
        }
    }
}

export function recoverChats(chats: IChat[]): ChatAction {
    return {
        type: ChatTypes.RECOVER_CHATS,
        payload: chats
    }
}

export function sendChatMessage(id: string, msg: string, clearEditor = true): ChatAction {
    return {
        type: ChatTypes.SEND_CHAT_MESSAGE,
        payload: {
            id,
            msg,
            clearEditor
        }
    }
}

export function updateConnectionEditorContent (id: string, content: EditorState): ChatAction {
    return {
        type: ChatTypes.UPDATE_CONNECTION_EDITOR_CONTENT,
        payload: {
            id,
            content
        }
    }
}

export function setSelectedChat(id: string | null): ChatAction {
    return {
        type: ChatTypes.SET_SELECTED_CHAT,
        payload: id
    }
}

export function addChatConnection(chat: IChat): ChatAction {
    return {
        type: ChatTypes.ADD_CHAT_CONNECTION,
        payload: chat
    }
}

export function recieveChatMessage(id: string, msg: IChatMessage) {

    return async (
        dispatch: ThunkDispatch<RootState, undefined, ChatAction>,
        getState: () => RootState
    ) => {

        let message : IChatMessage | IChatMessage[] = msg;

        batch(() => {
            // check this chat is currently selected
            if (id !== getState().contact?.ID) {
                const chatConnection = getState().chat.connections.find(c => c.id === id);

                !!chatConnection && (dispatch(
                    setUnreadChats(id, chatConnection?.unread)
                ))
            }

            // 
            if (typeof msg.content !== "string" && msg.content?.type === "BATCH") {
                message = msg.content.messages.map(batchMessage => ({
                    ...msg,
                    content: batchMessage
                }))
                
            }

            dispatch({
                type: ChatTypes.RECIEVE_CHAT_MESSAGE,
                payload: {
                    id,
                    msg: message
                }
            })
        })
    }

}

export function chatACW(id: string): ChatAction {
    return {
        type: ChatTypes.CHAT_ACW,
        payload: id
    }
}
export function extendChatACW(id: string): ChatAction {
    return {
        type: ChatTypes.CHAT_ACW_EXTEND,
        payload: id
    }
}

export function chatConnected(id: string): ChatAction {
    return {
        type: ChatTypes.CHAT_CONNECTED,
        payload: id
    }
}

export function chatDisconnected(id: string): ChatAction {
    return {
        type: ChatTypes.CHAT_DISCONNECTED,
        payload: id
    }
}

export function removeChatConnection(id: string): ChatAction {
    return {
        type: ChatTypes.REMOVE_CHAT_CONNECTION,
        payload: id
    }
}
//This action need for removing chat connection from store without following .destroy() api call
// which present in listener on REMOVE_CHAT_CONNECTION action
export function clearChatConnection(id: string): ChatAction {
    return {
        type: ChatTypes.CLEAR_CHAT_CONNECTION,
        payload: id,
    };
}

export function acceptChat(id: string): ChatAction {
    return {
        type: ChatTypes.ACCEPT_CHAT,
        payload: id
    }
}


export function declineChat(id: string): ChatAction {
    return {
        type: ChatTypes.DECLINE_CHAT,
        payload: id
    }
}

export function transferChat(id: string, endpoint: connect.Endpoint): ChatAction {
    return {
        type: ChatTypes.TRANSFER_CHAT,
        payload: {
            id,
            endpoint
        }
    }
}

export function markChatMessageReceiptsAsRead(id: string): ChatAction {
    return {
        type: ChatTypes.MARK_CHAT_MESSAGE_RECEIPTS_AS_READ,
        payload: id,
    }
}

export function updateDPAResultChat(result: DPAResult, id: string): ChatAction {
    return {
        type: ChatTypes.UPDATE_DPA_RESULT_CHAT,
        payload: {
            id,
            result
        }
    };
}

export function updateAgentDPAResultChat(check: DPACheck, id: string): ChatAction {
    return {
        type: ChatTypes.UPDATE_AGENT_DPA_CHAT,
        payload: {
            id,
            check
        }
    };
}

export function updateDPAChat(check: DPACheck, id: string): ChatAction {
    return {
        type: ChatTypes.UPDATE_DPA_CHAT,
        payload: {
            id,
            check
        }
    };
}

export function updateNote(id: string, note: string): ChatAction {
    return {
        type: ChatTypes.UPDATE_NOTE,
        payload: {
            id,
            note,
        },
    };
}

export function setCustomerActive(id: string) {
    return {
        type: ChatTypes.SET_CUSTOMER_INITIALLY_ACTIVE,
        payload: id
    }
}

